import React, { useMemo, useState } from 'react';
import { CircleNotchIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from 'src/components/ui/command';
import { CoreEnumOptionDTO } from 'src/api/core/core-enums/useCoreEnums';
import { CoreEnumOptionAssociation } from 'src/api/core/enumOptionAssociations/useEnumOptionAssociations';

interface TagAssociation {
  id: string;
  coreEnumOptionId: string;
  coreEnumOption?: {
    id: string;
    name: string | null;
    description: string | null;
    sortOrder: number;
    enumId: string;
  };
}

/**
 * Hook to extract tag name from a tag association
 * Memoized for performance
 */
export function useTagName(
  tag: TagAssociation | CoreEnumOptionAssociation,
  availableTags: CoreEnumOptionDTO[],
  fallback = 'Unknown',
): string {
  return useMemo(() => {
    return (
      tag.coreEnumOption?.name ||
      availableTags.find((opt) => opt.id === tag.coreEnumOptionId)?.name ||
      fallback
    );
  }, [tag.coreEnumOption?.name, tag.coreEnumOptionId, availableTags, fallback]);
}

interface TagBadgeProps {
  tag: TagAssociation | CoreEnumOptionAssociation;
  availableTags: CoreEnumOptionDTO[];
  variant?: 'default' | 'secondary' | 'outline';
  size?: 'sm' | 'md';
  showRemove?: boolean;
  onRemove?: (tagId: string) => void;
  isUpdating?: boolean;
  className?: string;
  fallbackName?: string;
}

/**
 * Reusable component for displaying a tag badge
 */
export function TagBadge({
  tag,
  availableTags,
  variant = 'default',
  size = 'md',
  showRemove = false,
  onRemove,
  isUpdating = false,
  className,
  fallbackName = 'Unknown',
}: TagBadgeProps) {
  const gt = useGT();
  const tagName = useTagName(tag, availableTags, fallbackName);

  const sizeClasses =
    size === 'sm' ? 'gap-1 pl-2 pr-1 text-xs' : 'gap-2 pl-3 pr-2';
  const iconSize = size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3';

  return (
    <Badge variant={variant} className={`${sizeClasses} ${className || ''}`}>
      <span>{tagName}</span>
      {showRemove && onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(tag.coreEnumOptionId);
          }}
          disabled={isUpdating}
          className="inline-flex hover:opacity-70 transition-opacity"
          title={gt('Remove tag', {
            context: 'tooltip on remove tag button',
          })}
        >
          <XIcon className={iconSize} />
        </button>
      )}
    </Badge>
  );
}

export function StaticTagBadge({ tag }: { tag: string }) {
  return (
    <Badge
      variant="secondary"
      className="gap-1 pl-2 pr-1 text-xs border-dashed border"
    >
      {tag}
    </Badge>
  );
}
interface TagsDisplayProps {
  currentTags: TagAssociation[];
  availableTags: CoreEnumOptionDTO[];
  currentTagIds: Set<string>;
  isLoading: boolean;
  isUpdating: boolean;
  maxTags?: number;
  onAddTag: (tagId: string) => void;
  onRemoveTag: (tagId: string) => void;
  /**
   * Create a brand-new tag from typed text. When provided, the compact
   * "Add Tag" picker shows a "Create" action so users can add tags to the
   * underlying enum on the fly.
   */
  onCreateTag?: (name: string) => void | Promise<void>;
  isCreatingTag?: boolean;
  layout?: 'inline' | 'compact'; // 'inline' for header/modal, 'compact' for rule cards
  showEmptyState?: boolean;
  staticTags?: string[];
  lockedTagIds?: Set<string>; // Tag IDs that cannot be added or removed
}

export function TagsDisplay({
  currentTags,
  availableTags,
  currentTagIds,
  isLoading,
  isUpdating,
  maxTags,
  onAddTag,
  onRemoveTag,
  onCreateTag,
  isCreatingTag = false,
  layout = 'compact',
  showEmptyState = true,
  staticTags,
  lockedTagIds = new Set(),
}: TagsDisplayProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <CircleNotchIcon className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          <T context="loading state for tags list">Loading tags...</T>
        </span>
      </div>
    );
  }

  const maxTagCount = maxTags ?? Infinity;
  const canAddMore = currentTags.length < maxTagCount;

  // Filter out locked tags from available tags (users can't add them)
  const unappliedTags = availableTags.filter(
    (tag) => !currentTagIds.has(tag.id) && !lockedTagIds.has(tag.id),
  );

  const handleAddTag = (tagId: string) => {
    if (!canAddMore) return;
    onAddTag(tagId);
  };

  if (layout === 'inline') {
    // For header and modal: show applied tags and available tags as separate sections
    return (
      <div className="space-y-3">
        {/* Applied Tags - Regular tags and static tags inline */}
        {currentTags.length > 0 || (staticTags && staticTags.length > 0) ? (
          <div className="flex flex-wrap gap-2">
            {currentTags.map((tag) => {
              const isLocked = lockedTagIds.has(tag.coreEnumOptionId);
              return (
                <TagBadge
                  key={tag.id}
                  tag={tag}
                  availableTags={availableTags}
                  variant="default"
                  size="md"
                  showRemove={!isLocked}
                  onRemove={!isLocked ? onRemoveTag : undefined}
                  isUpdating={isUpdating}
                  fallbackName="Unknown Tag"
                />
              );
            })}
            {staticTags?.map((tag) => (
              <StaticTagBadge key={tag} tag={tag} />
            ))}
          </div>
        ) : (
          showEmptyState && (
            <p className="text-sm text-muted-foreground">
              <T context="empty state when no tags on a record">
                No tags applied
              </T>
            </p>
          )
        )}

        {/* Available Tags to Add */}
        {availableTags.length > 0 && unappliedTags.length > 0 && canAddMore && (
          <div className="flex flex-wrap gap-2">
            {unappliedTags.map((tag) => (
              <Button
                key={tag.id}
                variant="outline"
                size="sm"
                onClick={() => handleAddTag(tag.id)}
                disabled={isUpdating}
                className="gap-2"
              >
                <PlusIcon className="h-3 w-3" />
                {tag.name}
              </Button>
            ))}
          </div>
        )}

        {availableTags.length === 0 && currentTags.length === 0 && (
          <div className="p-3 bg-muted rounded-lg text-center">
            <p className="text-sm text-muted-foreground">
              <T context="hint when org has no tags configured">
                No tags available. Create tags in organization settings to use
                them here.
              </T>
            </p>
          </div>
        )}
      </div>
    );
  }

  // Compact layout: for rule cards - inline with applied tags and dropdown for adding
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Applied Tags */}
      {currentTags.map((tag) => {
        const isLocked = lockedTagIds.has(tag.coreEnumOptionId);
        return (
          <TagBadge
            key={tag.id}
            tag={tag}
            availableTags={availableTags}
            variant="default"
            size="sm"
            showRemove={!isLocked}
            onRemove={!isLocked ? onRemoveTag : undefined}
            isUpdating={isUpdating}
            fallbackName="Unknown"
          />
        );
      })}
      {staticTags?.map((tag) => (
        <StaticTagBadge key={tag} tag={tag} />
      ))}
      {/* Add Tag — searchable picker, with optional "create new tag" action */}
      {canAddMore && (unappliedTags.length > 0 || onCreateTag) && (
        <AddTagPopover
          unappliedTags={unappliedTags}
          isUpdating={isUpdating}
          isCreatingTag={isCreatingTag}
          onAddTag={handleAddTag}
          onCreateTag={onCreateTag}
        />
      )}
    </div>
  );
}

interface AddTagPopoverProps {
  unappliedTags: CoreEnumOptionDTO[];
  isUpdating: boolean;
  isCreatingTag?: boolean;
  onAddTag: (tagId: string) => void;
  onCreateTag?: (name: string) => void | Promise<void>;
}

/**
 * Compact "Add Tag" control: a searchable list of the unapplied tags plus an
 * optional "Create" footer that creates a new tag from the typed text.
 */
function AddTagPopover({
  unappliedTags,
  isUpdating,
  isCreatingTag = false,
  onAddTag,
  onCreateTag,
}: AddTagPopoverProps) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const trimmed = query.trim();
  const lowerQuery = trimmed.toLowerCase();
  const filtered = trimmed
    ? unappliedTags.filter((tag) =>
        (tag.name ?? '').toLowerCase().includes(lowerQuery),
      )
    : unappliedTags;
  const exactExists = unappliedTags.some(
    (tag) => (tag.name ?? '').trim().toLowerCase() === lowerQuery,
  );
  const canCreate = Boolean(onCreateTag) && trimmed.length > 0 && !exactExists;

  const handleSelect = (tagId: string) => {
    onAddTag(tagId);
    setQuery('');
    setOpen(false);
  };

  const handleCreate = async () => {
    if (!onCreateTag || !trimmed) return;
    await onCreateTag(trimmed);
    setQuery('');
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => e.stopPropagation()}
          disabled={isUpdating || isCreatingTag}
          className="gap-1 h-6 px-2 text-xs"
        >
          <PlusIcon className="h-3 w-3" />
          <T context="button to add a tag">Add Tag</T>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-56 p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={gt('Search or create…', {
              context: 'placeholder for the searchable add-tag input',
            })}
          />
          <CommandList>
            {filtered.length === 0 && !canCreate && (
              <CommandEmpty>
                <T context="empty state when no tags match the search">
                  No tags found.
                </T>
              </CommandEmpty>
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((tag) => (
                  <CommandItem
                    key={tag.id}
                    value={tag.id}
                    onSelect={() => handleSelect(tag.id)}
                    disabled={isUpdating}
                  >
                    {tag.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {canCreate && (
              <>
                {filtered.length > 0 && <CommandSeparator />}
                <CommandGroup>
                  <CommandItem
                    value={`__create__${trimmed}`}
                    onSelect={handleCreate}
                    disabled={isCreatingTag}
                  >
                    <PlusIcon className="h-3 w-3" />
                    <span>
                      {gt('Create "{name}"', {
                        context:
                          'menu action to create a new tag from typed text',
                        name: trimmed,
                      })}
                    </span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
